// ---------------------------------------------------------------------------
// ObservationSinkIsland — side-effect-only component that forwards brainstorm
// events from the Zustand store to the provider's recordObservation endpoint.
//
// Registered as `studio.brainstorm.observation-sink` in the host IslandRegistry.
// Renders null (invisible). Subscribes to `brainstorm.events` via
// useStudioStore.subscribe (subscribeWithSelector middleware) and forwards
// any new events to the provider. Errors are swallowed with console.warn
// (graceful handling for v1 NotImplemented).
// ---------------------------------------------------------------------------

import { useEffect, useRef } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { getBrainstormProvider } from '../providers'
import type { BrainstormQuery } from '../repository'

interface ObservationSinkIslandProps {
  /** Epoch ms when the brainstorm session started — used for dwell tracking. */
  dwellStartMs?: number
}

export function ObservationSinkIsland({ dwellStartMs: _dwellStartMs = 0 }: ObservationSinkIslandProps) {
  const lastSeenRef = useRef(0)

  useEffect(() => {
    const provider = getBrainstormProvider()

    const unsub = useStudioStore.subscribe(
      (s) => s.brainstorm.events,
      (_events) => {
        const state = useStudioStore.getState()
        const events = state.brainstorm.events
        for (let i = lastSeenRef.current; i < events.length; i++) {
          const ev = events[i]!
          const query = state.brainstorm.query as BrainstormQuery

          const picked =
            ev.kind === 'pick' && ev.candidate
              ? { exampleId: (ev.candidate as { exampleId: string }).exampleId }
              : null

          provider
            .recordObservation({
              kind: ev.kind,
              query,
              picked,
              rejected: [],
              dwellMs: ev.dwellMs,
              expandedIds: [],
              session: 'studio-session',
              timestamp: ev.timestamp,
            })
            .catch((err: unknown) => {
              console.warn('[brainstorm] recordObservation failed:', err)
            })
        }
        lastSeenRef.current = events.length
      },
    )

    return unsub
  }, [])

  return null
}
